import { loadStripe, Stripe } from '@stripe/stripe-js';
import { app } from './LoginService';
import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';

// TODO: Replace with your actual Stripe publishable key
// Get this from: https://dashboard.stripe.com/apikeys
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Get Stripe instance (singleton)
 */
export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

export interface CreateCheckoutSessionParams {
  type: 'monthly' | 'one-time';
  amount: number;
  newsletter?: boolean;
  groupId?: string;
  artistId?: string;
  productName?: string;
  productDescription?: string;
}

export interface GroupCheckoutParams {
  groupId: string;
  artistId: string;
  amount: number;
  productName?: string;
  productDescription?: string;
}

export interface CheckoutSessionResponse {
  sessionId?: string;
  url?: string;
}

/**
 * Create a Stripe Checkout session via Firebase Cloud Function
 */
export const createCheckoutSession = async (
  params: CreateCheckoutSessionParams
): Promise<CheckoutSessionResponse> => {
  try {
    // Get Firebase Functions instance
    const functions = firebase.functions(app);

    // Call the Cloud Function
    const createStripeCheckoutSession = functions.httpsCallable('createStripeCheckoutSession');
    const result = await createStripeCheckoutSession({
      type: params.type,
      amount: params.amount,
      newsletter: params.newsletter,
      groupId: params.groupId,
      artistId: params.artistId,
      productName: params.productName,
      productDescription: params.productDescription,
    });

    return result.data as CheckoutSessionResponse;
  } catch (error: any) {
    console.error('Error creating checkout session:', error);
    throw new Error(error.message || 'Failed to create checkout session');
  }
};

/**
 * Redirect to Stripe Checkout
 */
export const redirectToCheckout = async (
  params: CreateCheckoutSessionParams
): Promise<void> => {
  // Create checkout session via Firebase Cloud Function
  const response = await createCheckoutSession(params);

  // If the backend returns a URL, redirect directly to it
  if (response.url) {
    window.location.href = response.url;
    return;
  }

  // Otherwise, use the session ID with Stripe.js (legacy approach)
  if (response.sessionId) {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe failed to load');
    }

    const { error } = await (stripe as any).redirectToCheckout({
      sessionId: response.sessionId,
    });

    if (error) {
      console.error('Stripe redirect error:', error);
      throw error;
    }
  } else {
    throw new Error('No session ID or URL returned from checkout session creation');
  }
};

/**
 * Redirect to Stripe Checkout for a paid group-join flow (e.g. /coursera):
 * a one-time payment that, once confirmed by the webhook, adds `artistId`
 * to `groupId`'s member list.
 */
export const redirectToGroupCheckout = async (
  params: GroupCheckoutParams
): Promise<void> => {
  await redirectToCheckout({
    type: 'one-time',
    amount: params.amount,
    groupId: params.groupId,
    artistId: params.artistId,
    productName: params.productName,
    productDescription: params.productDescription,
  });
};

/**
 * Confirm a completed group-join Checkout session and get added to the
 * group — called right when the page comes back from Stripe with
 * ?session_id=..., instead of waiting on a Stripe webhook.
 */
export const confirmGroupCheckout = async (sessionId: string): Promise<{ groupId: string }> => {
  const functions = firebase.functions(app);
  const confirm = functions.httpsCallable('confirmGroupCheckout');
  const result = await confirm({ sessionId });
  return result.data as { groupId: string };
};

export const StripeService = {
  getStripe,
  createCheckoutSession,
  redirectToCheckout,
  redirectToGroupCheckout,
  confirmGroupCheckout,
};

export default StripeService;
